// ABOUTME: Component that prompts users to add the TradesPurple app to their home screen
// ABOUTME: Provides custom installation instructions and QR code based on the device

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Share, PlusCircle, ChevronDown, Smartphone, QrCode } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Type for navigator.standalone in iOS Safari
interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

export function AddToHomeScreenPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [hasUserDismissed, setHasUserDismissed] = useState(false);

  // Check if running in PWA mode and detect device type
  useEffect(() => {
    // Check if iOS device
    const checkIOS = () => {
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      setIsIOS(iOS);
    };

    // Check if desktop device
    const checkDesktop = () => {
      // Check if device has a large screen and no touch capability
      const isLargeScreen = window.innerWidth > 768;
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsDesktop(isLargeScreen && !hasTouch);
    };

    // Check if already installed as PWA
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as SafariNavigator).standalone === true;
      setIsStandalone(standalone);
    };

    // Check if user has previously dismissed the prompt
    const checkDismissed = () => {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (dismissed) {
        // Only respect dismissal for 7 days
        const dismissedTime = parseInt(dismissed, 10);
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - dismissedTime < sevenDaysInMs) {
          setHasUserDismissed(true);
        } else {
          // Reset if it's been more than 7 days
          localStorage.removeItem('pwa-prompt-dismissed');
        }
      }
    };

    checkIOS();
    checkDesktop();
    checkStandalone();
    checkDismissed();

    // Show prompt after delay - shorter for desktop, immediate if from install URL
    if (!isStandalone && !hasUserDismissed) {
      const isFromInstallUrl = window.location.pathname === '/install';
      // Check desktop state directly since we just set it
      const desktop = window.innerWidth > 768 && !('ontouchstart' in window || navigator.maxTouchPoints > 0);
      const delay = isFromInstallUrl ? 0 : (desktop ? 30000 : 60000); // 0s if from /install, 30s desktop, 60s mobile

      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isStandalone, hasUserDismissed]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowDialog(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setHasUserDismissed(true);
  };

  const handleShowInstructions = () => {
    setShowPrompt(false);
    setShowDialog(true);
  };

  // Don't show if already in standalone mode or user dismissed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <>
      {/* Bottom prompt */}
      {showPrompt && (
        <div className={`fixed ${isDesktop ? 'bottom-8 right-8' : 'bottom-0 left-0 right-0'} z-50 ${isDesktop ? '' : 'p-3'}`}>
          <Card className={`shadow-lg border-purple-200 bg-purple-50 ${isDesktop ? 'w-80' : 'max-w-sm mx-auto'} animate-in ${isDesktop ? 'slide-in-from-right' : 'slide-in-from-bottom'} duration-300`}>
            <CardHeader className={`${isDesktop ? 'pb-2' : 'pb-1 pt-3'} relative`}>
              <Button
                variant="ghost"
                size="icon"
                className={`absolute ${isDesktop ? 'right-2 top-2' : 'right-1 top-1'} text-purple-700 hover:bg-purple-100 hover:text-purple-800`}
                onClick={handleDismiss}
              >
                <X className={isDesktop ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
              </Button>
              <CardTitle className={`text-purple-900 flex items-center ${isDesktop ? '' : 'text-base'}`}>
                {isDesktop ? <QrCode className="mr-2 h-5 w-5" /> : <Smartphone className="mr-2 h-4 w-4" />}
                {isDesktop ? 'Get TradesPurple on Your Phone' : 'Add to Home Screen'}
              </CardTitle>
            </CardHeader>
            <CardContent className={isDesktop ? 'text-center pb-4' : 'pb-1.5 pt-2'}>
              {isDesktop ? (
                <>
                  <div className="mb-4 bg-white p-4 rounded-lg">
                    <img
                      src="/tradespurple/TP-QR.png"
                      alt="QR Code for TradesPurple"
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                  <p className="text-sm text-purple-700">
                    Scan this QR code with your phone to install TradesPurple
                  </p>
                </>
              ) : (
                <p className="text-xs text-purple-700">
                  Install for a faster, app-like experience with offline access.
                </p>
              )}
            </CardContent>
            <CardFooter className={`flex ${isDesktop ? 'justify-center' : 'gap-2'} ${isDesktop ? 'pt-0' : 'pt-1 pb-3'}`}>
              {isDesktop ? (
                <Button
                  variant="outline"
                  className="text-sm border-purple-200 text-purple-700 hover:bg-purple-100 hover:text-purple-900"
                  onClick={handleDismiss}
                >
                  Dismiss
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-purple-200 text-purple-700 hover:bg-purple-100 hover:text-purple-900"
                    onClick={handleDismiss}
                  >
                    Not Now
                  </Button>
                  <Button
                    size="sm"
                    className="text-xs bg-purple-700 hover:bg-purple-800 text-white flex-1"
                    onClick={handleShowInstructions}
                  >
                    Install
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Installation instructions dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">
              Install TradesPurple
            </DialogTitle>
          </DialogHeader>

          {isIOS ? (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Follow these steps to add TradesPurple to your home screen:
              </p>

              <div className="space-y-4">
                <div className="border rounded-md p-3 flex items-start space-x-3">
                  <div className="bg-purple-100 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                    <Share className="h-5 w-5 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">1. Tap the Share button</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Tap the Share button in Safari's bottom toolbar.
                    </p>
                  </div>
                </div>

                <div className="border rounded-md p-3 flex items-start space-x-3">
                  <div className="bg-purple-100 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                    <PlusCircle className="h-5 w-5 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">2. Scroll and tap "Add to Home Screen"</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Scroll down in the share menu and tap the "Add to Home Screen" button.
                    </p>
                  </div>
                </div>

                <div className="border rounded-md p-3 flex items-start space-x-3">
                  <div className="bg-purple-100 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                    <ChevronDown className="h-5 w-5 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">3. Tap "Add" in the top right</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      You can edit the name before adding if you want. The app will appear on your home screen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Follow the prompt that appears in your browser or manually add TradesPurple to your home screen.
              </p>

              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs text-gray-500">
                  Note: If you don't see an installation prompt, this browser may not support adding web apps to the home screen.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-2">
            <Button
              className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={handleDismiss}
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AddToHomeScreenPrompt;
